import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

function Timeline() {
  const { t, i18n } = useTranslation();
  const [selectedYear, setSelectedYear] = useState(null);
  const [expandedCards, setExpandedCards] = useState(() => new Set());

  const events = useMemo(() => {
    const raw = t("timeline.events", { returnObjects: true });
    return Array.isArray(raw) ? raw : [];
  }, [t, i18n.language]);

  const sortedEvents = useMemo(
    () => [...events].sort((a, b) => a.year - b.year),
    [events]
  );

  useEffect(() => {
    if (!sortedEvents.length || selectedYear !== null) return;
    setSelectedYear(sortedEvents[0].year);
  }, [sortedEvents, selectedYear]);

  const leftEvents = sortedEvents.slice(0, 5);
  const rightEvents = sortedEvents.slice(5);

  const handleSelectYear = (year) => {
    setSelectedYear(year);
  };

  const handleCardToggle = (year) => {
    setExpandedCards((prev) => {
      if (prev.has(year)) return new Set();
      return new Set([year]);
    });
  };

  const selectedEvent = events.find((event) => event.year === selectedYear);

  const getYearsAgo = (year) => {
    const currentYear = new Date().getFullYear();
    return Math.max(0, currentYear - year);
  };

  /* Aktiver linker Rand in Primary-Blau wie restliche Site; inaktiv slate */
  const desktopButtonClasses = (isSelected) => {
    const base =
      "group relative w-full rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-sm transition-all duration-300 dark:border-slate-700 dark:bg-slate-800 md:p-6 " +
      "border-l-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-50 " +
      "dark:focus-visible:ring-primary-light/50 dark:focus-visible:ring-offset-slate-900 " +
      "motion-safe:hover:-translate-y-0.5 ";
    const state = isSelected
      ? "border-l-primary shadow-md motion-safe:hover:shadow-lg dark:border-l-primary-light"
      : "border-l-slate-300 motion-safe:hover:shadow-sm dark:border-l-slate-600";
    return `${base}${state}`;
  };

  const renderDesktopEventButton = (event) => {
    const isSelected = selectedYear === event.year;
    return (
      <button
        type="button"
        aria-pressed={isSelected}
        aria-controls="timeline-details"
        aria-label={t("timeline.select_event_aria", { title: event.title })}
        className={desktopButtonClasses(isSelected)}
        onClick={() => handleSelectYear(event.year)}
      >
        <div className="flex items-start gap-4">
          <span
            className={`flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl transition-colors duration-300 ${
              isSelected
                ? "bg-primary text-white shadow-md dark:bg-primary-light dark:text-slate-900"
                : "bg-primary/10 text-primary dark:bg-primary-light/15 dark:text-primary-light motion-safe:group-hover:scale-[1.02]"
            }`}
            aria-hidden
          >
            <i className={`${event.icon} text-xl`} />
          </span>

          <div className="min-w-0 flex-1 overflow-hidden">
            <h3
              className={`break-words text-base font-semibold leading-snug ${
                isSelected
                  ? "text-primary dark:text-primary-light"
                  : "text-slate-900 dark:text-slate-100"
              }`}
            >
              {event.title}
            </h3>
            <p className="mt-1.5 break-words text-sm leading-relaxed text-slate-600 line-clamp-3 dark:text-slate-400">
              {event.description}
            </p>
          </div>
        </div>
      </button>
    );
  };

  const renderMobileEventItem = (event) => {
    const isExpanded = expandedCards.has(event.year);
    const panelId = `timeline-panel-${event.year}`;
    const triggerId = `timeline-trigger-${event.year}`;

    const baseCard =
      "group relative w-full rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-sm transition-all duration-300 dark:border-slate-700 dark:bg-slate-800 " +
      "border-l-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-50 " +
      "dark:focus-visible:ring-primary-light/50 dark:focus-visible:ring-offset-slate-900 " +
      "motion-safe:hover:shadow-md ";

    const stateClasses = isExpanded
      ? "border-l-primary shadow-md dark:border-l-primary-light"
      : "border-l-slate-300 dark:border-l-slate-600";

    return (
      <li key={event.year} className="list-none">
        <button
          type="button"
          id={triggerId}
          aria-expanded={isExpanded}
          aria-controls={panelId}
          aria-label={t("timeline.toggle_event_aria", { title: event.title })}
          className={`${baseCard} ${stateClasses}`}
          onClick={() => handleCardToggle(event.year)}
        >
          <div className="flex items-start gap-4">
            <span
              className={`flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl transition-colors duration-300 ${
                isExpanded
                  ? "bg-primary text-white shadow-md dark:bg-primary-light dark:text-slate-900"
                  : "bg-primary/10 text-primary dark:bg-primary-light/15 dark:text-primary-light group-hover:bg-primary/15 dark:group-hover:bg-primary-light/25"
              }`}
              aria-hidden
            >
              <i className={`${event.icon} text-xl`} />
            </span>

            <div className="min-w-0 flex-1 overflow-hidden">
              <h3
                className={`break-words text-base font-semibold leading-snug ${
                  isExpanded
                    ? "text-primary dark:text-primary-light"
                    : "text-slate-900 dark:text-slate-100"
                }`}
              >
                {event.title}
              </h3>
              <p className="mt-1.5 break-words text-sm leading-relaxed text-slate-600 line-clamp-3 dark:text-slate-400">
                {event.description}
              </p>
            </div>

            <span
              className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-slate-500 dark:border-slate-600 dark:bg-slate-900/60 dark:text-slate-400"
              aria-hidden
            >
              <i
                className={`fas text-sm ${isExpanded ? "fa-chevron-up" : "fa-chevron-down"}`}
              />
            </span>
          </div>
        </button>

        <div
          id={panelId}
          role="region"
          aria-labelledby={triggerId}
          hidden={!isExpanded}
          className={
            isExpanded
              ? "mt-3 rounded-2xl border border-slate-200 border-l-4 border-l-primary/90 bg-slate-50/90 p-5 dark:border-slate-700 dark:border-l-primary-light/90 dark:bg-slate-900/50"
              : "hidden"
          }
        >
          <p className="break-words text-sm leading-relaxed text-slate-700 dark:text-slate-300 md:text-base">
            {event.details}
          </p>
          {event.year < new Date().getFullYear() && (
            <p className="mt-4 flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
              <i className="fas fa-history" aria-hidden />
              {t("timeline.years_ago", { count: getYearsAgo(event.year) })}
            </p>
          )}
        </div>
      </li>
    );
  };

  const renderDetailsBox = ({ fillHeight = false } = {}) => (
    <div
      id="timeline-details"
      role="region"
      aria-live="polite"
      aria-labelledby="timeline-detail-title"
      className={
        "flex min-w-0 flex-col overflow-hidden rounded-2xl border border-slate-200 border-l-4 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800 " +
        (selectedEvent
          ? "border-l-primary dark:border-l-primary-light shadow-md"
          : "border-l-slate-300 dark:border-l-slate-600") +
        (fillHeight ? " h-full min-h-0 flex-1" : " min-h-[280px]")
      }
    >
      {selectedEvent ? (
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden p-6 md:p-8">
          <div className="flex min-w-0 flex-col gap-5 sm:flex-row sm:items-start">
            <span
              className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl bg-primary/10 dark:bg-primary-light/15"
              aria-hidden
            >
              <i
                className={`${selectedEvent.icon} text-xl text-primary dark:text-primary-light`}
              />
            </span>
            <div className="min-w-0 flex-1 space-y-3 break-words">
              <div>
                <h3
                  id="timeline-detail-title"
                  className="break-words text-xl font-semibold tracking-tight text-slate-900 dark:text-slate-100 sm:text-2xl"
                >
                  {selectedEvent.title}
                </h3>
                <p className="mt-2 text-sm font-medium leading-relaxed text-slate-600 dark:text-slate-400">
                  {selectedEvent.description}
                </p>
              </div>
              <div className="border-t border-slate-200 pt-5 dark:border-slate-700">
                <p className="break-words text-sm leading-relaxed text-slate-700 dark:text-slate-300 md:text-base">
                  {selectedEvent.details}
                </p>
              </div>
              {selectedEvent.year < new Date().getFullYear() && (
                <p className="flex items-center gap-2 pt-1 text-xs text-slate-500 dark:text-slate-400">
                  <i className="fas fa-history" aria-hidden />
                  {t("timeline.years_ago", {
                    count: getYearsAgo(selectedEvent.year),
                  })}
                </p>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-6 py-12 text-center">
          <span
            className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100 dark:bg-slate-700/80"
            aria-hidden
          >
            <i className="fas fa-info-circle text-2xl text-slate-400 dark:text-slate-500" />
          </span>
          <p
            id="timeline-detail-title"
            className="max-w-sm text-sm leading-relaxed text-slate-600 dark:text-slate-400"
          >
            {t("timeline.defaultDetailsMessage")}
          </p>
        </div>
      )}
    </div>
  );

  return (
    <section
      className="relative z-10 bg-slate-50/50 pb-14 pt-12 transition-colors dark:bg-slate-900 md:pb-16 md:pt-14"
      id="timeline"
      aria-labelledby="timeline-heading"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <header className="mb-10 text-center md:mb-12">
          <h2
            id="timeline-heading"
            className="relative mb-5 inline-block text-4xl font-semibold tracking-tight text-primary dark:text-primary-light md:text-5xl"
          >
            {t("timeline.title")}
            <span
              className="absolute -bottom-2.5 left-1/2 h-0.5 w-16 -translate-x-1/2 rounded-full bg-primary dark:bg-primary-light"
              aria-hidden
            />
          </h2>
          <p className="mx-auto mt-5 max-w-3xl text-lg leading-relaxed text-slate-600 dark:text-slate-400">
            {t("timeline.description")}
          </p>
        </header>

        <ul className="m-0 list-none space-y-4 p-0 md:hidden">
          {sortedEvents.map((event) => renderMobileEventItem(event))}
        </ul>

        <div className="hidden md:block xl:hidden">
          <ul
            className="mb-6 grid list-none grid-cols-1 gap-4 p-0 md:grid-cols-2 md:gap-5"
            aria-label={t("timeline.events_list_label")}
          >
            {sortedEvents.map((event) => (
              <li key={event.year} className="min-w-0 list-none">
                {renderDesktopEventButton(event)}
              </li>
            ))}
          </ul>
          {renderDetailsBox({ fillHeight: false })}
        </div>

        <div className="hidden min-h-0 xl:grid xl:grid-cols-3 xl:items-stretch xl:gap-8">
          <ul
            className="m-0 flex h-full min-h-0 min-w-0 list-none flex-col gap-4 p-0"
            aria-label={t("timeline.events_list_label")}
          >
            {leftEvents.map((event) => (
              <li key={event.year} className="min-w-0 list-none">
                {renderDesktopEventButton(event)}
              </li>
            ))}
          </ul>

          <div className="flex h-full min-h-0 min-w-0 flex-col self-stretch">
            {renderDetailsBox({ fillHeight: true })}
          </div>

          <ul
            className="m-0 flex h-full min-h-0 min-w-0 list-none flex-col gap-4 p-0"
            aria-label={t("timeline.events_list_label_right")}
          >
            {rightEvents.map((event) => (
              <li key={event.year} className="min-w-0 list-none">
                {renderDesktopEventButton(event)}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

export default Timeline;
